import {SRC} from "/@lib@/wsp/src.js";
import {IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {WSP} from "/@lib@/wsp/wsp.js";
import {SrcAction} from "/@back@/wsp/actions/srcActions.js";
import {EItemTypeFamily, IDatasResp} from "/@lib@/wsp/wspMetaUi.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {LANG} from "/@lib@/commons/lang.js";
import "/@back@/wsp/actions/actions_Perms.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {ActionMenuDep, IAction} from "/@lib@/commons/actions.js";

/**
 * Remplace le contenu d'une ressource par un fichier sélectionné sur le système de fichier
 */
export class ImportRes<C extends IShortDescCtx> extends SrcAction<C> {

	static SINGLETON = () => ImportRes._S || (ImportRes._S = new ImportRes());
	private static _S: ImportRes<IShortDescCtx>;

	constructor(id?: string) {
		super(id || "importSrc");
		this._group = "importExport";
		this._icon = "/@skin@/wsp/actions/impFile.svg";
		this._label = "Remplacer..."; //On garde le mot "Importer" pour les actions d'imports qui aboutissent à des créations (voire des remplacement mais après double confirmation).
		this.exactOne = true;
		this.families = [EItemTypeFamily.res /* A trancher : , EItemTypeFamily.xml*/];
		this._enablePerms = "action.item#import.resFile";
	}

	/** Ouvre la boîte de dialogue de sélection de fichier et lance l'upload */
	async execute(ctx: C, ev?: Event): Promise<void> {
		const [shortDesc] = ctx.shortDescs;
		if (!shortDesc) return;
		const {env} = ctx.reg;

		const input = document.createElement('input');
		input.type = "file";
		const itemType = env.wsp.wspMetaUi.getItemType(shortDesc.itModel);
		const exts = itemType.getExtensions();
		input.accept = Array.isArray(exts) ? exts.join(',') : exts;
		input.click();
		const file = await new Promise<File>((resolve) => {
			input.onchange = function (this: HTMLInputElement) {
				resolve(this.files.length ? this.files[0] : null);
			}
		});
		if (!file) return;
		const progress = POPUP.showProgress(ctx.emitter, "Import en cours...");
		try {
			await ITEM.update(env.wsp, ctx.emitter, ctx.shortDescs[0].srcUri, file, progress.progressCallback);
			progress.close();
		} catch (e) {
			progress.close();
			await ERROR.report("Une erreur est survenue lors de l'import de la ressource.", e, ctx);
		}
	}
}

/**
 * Remplace le contenu d'une ressource de type dossier par un dossier sélectionné sur le système de fichier
 */
export class ImportFolder<C extends IShortDescCtx> extends SrcAction<C> {

	static SINGLETON = () => ImportFolder._S || (ImportFolder._S = new ImportFolder());
	private static _S: ImportFolder<IShortDescCtx>;

	constructor(id?: string) {
		super(id || "importSrc");
		this._group = "importExport";
		this._label = "Importer un dossier...";
		this.exactOne = true;
		this.families = [EItemTypeFamily.folder];
	}

	/** Ouvre la boîte de dialogue de sélection de fichier et lance l'upload */
	async execute(ctx: IShortDescCtx, ev?: Event): Promise<void> {
		const [shortDesc] = ctx.shortDescs;
		if (!shortDesc) return;
		const {env} = ctx.reg;

		const input = document.createElement('input');
		input.type = "file";
		input.setAttribute('webkitdirectory', '');
		input.setAttribute('directory', '');
		input.click();
		const files = await new Promise<FileList>((resolve) => {
			input.onchange = function (this: HTMLInputElement) {
				resolve(this.files);
			}
		});
		if (!files.length) return;
		const progress = POPUP.showProgress(ctx.emitter, "Import en cours...");
		let fileDone = 0;
		try {
			await LANG.promisesMap(files, async (file) => {
				const relPath = file.webkitRelativePath.substr(file.webkitRelativePath.indexOf('/') + 1);
				await ITEM.update(env.wsp, ctx.emitter, `${ctx.shortDescs[0].srcUri}/${relPath}`, file);
				progress.onProgress(++fileDone, files.length + 1);
			}, 4);
			progress.close();
		} catch (e) {
			progress.close();
			await ERROR.report("Une erreur est survenue lors de l'import du dossier.", e, ctx);
		}
	}
}

/**
 * Démarre le téléchargement d'une ressource vers le système de fichier
 */
export class ExportRes<C extends IShortDescCtx> extends SrcAction<C> {

	static SINGLETON = () => ExportRes._S || (ExportRes._S = new ExportRes());
	private static _S: ExportRes<IShortDescCtx>;

	constructor(id?: string) {
		super(id || "downloadSrc");
		this._group = "importExport";
		this._label = "Exporter vers un fichier...";
		this._icon = "/@skin@/wsp/actions/expFile.svg";
		this.exactOne = true;
		this.noFamilies = [EItemTypeFamily.folder];
		this._enableSrcPerms = ["read.node.export"];
	}

	/** Ouvre la boîte de dialogue de sélection de fichier et lance l'upload */
	async execute(ctx: C, ev?: Event): Promise<void> {
		const [shortDesc] = ctx.shortDescs;
		if (!shortDesc) return;
		const {env} = ctx.reg;
		await ctx.reg.env.wsp.wspServer.wspsLive.saveHouse(WSP.buildWspRef(env.wsp.code, shortDesc));
		const link = document.createElement('a');
		const itemType = env.wsp.wspMetaUi.getItemType(shortDesc.itModel);
		link.href = WSP.getStreamUrl(env.wsp, itemType.getMainStreamSrcUri(shortDesc)).url;
		link.download = SRC.extractLeafFromUri(shortDesc.srcUri);
		link.click();
	}
}


/**
 * Affecte des responsabilités sur un ensemble d'items
 */
export class ItemsSetResonsabilities<C extends IShortDescCtx> extends ActionMenuDep<C> {

	static SINGLETON = () => ItemsSetResonsabilities._S || (ItemsSetResonsabilities._S = new ItemsSetResonsabilities());
	private static _S: ItemsSetResonsabilities<IShortDescCtx>;

	constructor(id?: string) {
		super(id || 'setResonsability');
		this._label = "Modifier les responsabilités...";
		this._group = "edit";
		this.setVisible((ctx) => ctx.reg.env.wsp.hasFeature("resp") && ctx.reg.env.wsp.wspMetaUi.hasResps);
	}

	getActions(ctx: C): IAction<C>[] {
		const list: ItemsSetResponsability<C>[] = [];
		ctx.reg.env.wsp.wspMetaUi.listResps('item').forEach((resp) => list.push(new ItemsSetResponsability(resp)));
		return list;
	}

}


/**
 * Action d'une resp sur un ensemble d'items
 */
export class ItemsSetResponsability<C extends IShortDescCtx> extends SrcAction<C> {

	constructor(public resp: IDatasResp) {
		super(resp.code);
		this._label = resp.name;
		this._description = resp.desc;
		this.atLeastOne = true;
		this.setVisible((ctx) => ctx.reg.env.wsp.hasFeature("resp") && ctx.reg.env.wsp.wspMetaUi.hasResps);
		this.requireSrcEnabledPerm("action.item#set.resps");
	}

	isEnabled(ctx: C): boolean {
		if (!super.isEnabled(ctx)) return false;
		for (const sd of ctx.shortDescs) {
			const itemType = ctx.reg.env.wsp.wspMetaUi.getItemType(sd.itModel);
			if (!itemType.getResp(this.resp.code)) return false;
		}
		return true;
	}

	async execute(ctx: C, ev?: Event) {
		const reg = ctx.reg;
		const {SrcEditResp} = await import("/@back@/wsp/dialogs/srcEditResp.js");
		const nb = ctx.shortDescs.length;
		let itemsTitle = "";
		if (nb == 1) {
			itemsTitle = ITEM.getItemUriLabel(ctx.shortDescs[0].srcUri, ctx.shortDescs[0]);
		} else
			itemsTitle = `${nb} items`;
		const repName = this.resp.name;

		await POPUP.showDialog(new SrcEditResp().initialize({
			reg: ctx.reg,
			resp: this.resp,
			shortDescs: ctx.shortDescs,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: `Responsabilité '${repName}' sur ${itemsTitle}`}, closeButton: {}},
			resizer: {},
			initWidth: "min(40vw,30em)",
			initHeight: "20vh"
		}).onNextClose();

	}
}


